/**
 * lib/data/membership-checkout.ts
 *
 * The plan/price table the SUBSCRIPTION checkout modal renders, in MINOR units.
 *
 * ── Why this file exists ────────────────────────────────────────────────────
 * Before this, membership amounts lived as prose literals inside
 * `app/pro-member/_client/PMPricingFaqCtaClient.tsx` ("€32.90 / month",
 * "Billed as €395 per year", "Save €192…") and as separate raw numbers in
 * `lib/data/pricing.ts`. Two independent encodings of the same money. The modal
 * needs arithmetic (VAT split, savings, per-cycle totals), so it needs numbers,
 * not sentences — and one table is the only way the two surfaces can be flipped
 * together in the coordinated price round.
 *
 * ── 2026-09-14 PRICE ROUND (Lars's pricing + checkout spec §2.5 / §16) ──────
 * Four bands per currency, NO QUARTERLY (retired for new purchases; 0 active
 * subscribers per Sage's Step 0 snapshot). Rule 1 of the round: the charge is a
 * LOOKUP — `charge = lookup(plan, band, currency)` — never "EU price minus
 * VAT". €419 is stored as 41900; it is not €479 minus anything. These twelve
 * values mirror the staging `plan_prices` rows the pf-platform build migrates
 * (EUR eu 47900/5900, EUR row 41900/5900, USD eu 57000/6900, USD row 49900/6900).
 * Monthly carries no geo-split in amount: €59 / $69 in both bands.
 * The band a buyer is CHARGED is decided server-side from the card / wallet
 * country (ConfirmationToken). The band this site DISPLAYS comes from the IP
 * country against the EU-27 list (lib/price-band.ts). A mismatch is a swap
 * panel and a second tap, never a silent charge.
 *
 * ── Historical note (pre-2026-09-14), kept for provenance ─────────────────
 * ✅ THE COORDINATED ROUND RAN ON 2026-09-01 (Vela, brief: reopen Pro checkout).
 * These twelve amounts are now byte-identical to production `plan_prices`
 * (`pro_{interval}_{currency}_{eu|row}`) and to the twelve live-mode Stripe
 * prices those rows point at (all `tax_behavior=inclusive`, verified
 * 2026-09-01). The old €48.95 / €139.95 / €395 and $59 / $169 / $479 scheme is
 * retired on every surface in this repo in the same commit.
 *
 * The invariant that matters: THE NUMBER A BUYER SEES IS THE NUMBER THEY ARE
 * CHARGED. If you change a digit here, change `plan_prices.unit_amount` and the
 * Stripe price in the same breath, or the modal will DISPLAY one number and the
 * platform will CHARGE another.
 *
 * ── EU vs rest-of-world ─────────────────────────────────────────────────────
 * The design bands prices into `eu` and `row`, and since 2026-09-01 the two
 * bands carry DIFFERENT numbers: `eu` is the VAT-inclusive sticker, `row` is the
 * same net amount with no EU VAT inside it. The band a buyer actually falls into
 * is resolved SERVER-SIDE by `resolveTaxRegion()` in pf-platform, from the
 * country they typed into this form — never from the currency, never from their
 * IP, never from anything else the browser sends. What this table does is render
 * a PREVIEW of that decision, which is why the modal re-renders the total the
 * moment the country select changes.
 *
 * ── VAT ─────────────────────────────────────────────────────────────────────
 * Prices are tax-INCLUSIVE: an EU consumer pays the figure shown, full stop, and
 * the VAT lives inside it. So the display line splits the tax OUT of the number
 * (`gross − gross / (1 + rate)`), it never adds tax on top.
 */

export type Currency = 'EUR' | 'USD';
/** Internal plan keys. The checkout URL says `annual`; see `planFromParam`. */
export type PlanKey = 'monthly' | 'yearly';
export type PriceBand = 'eu' | 'row';

/** Minor units (cents). See the header before touching a single digit. */
export const MEMBERSHIP_PRICES: Record<Currency, Record<PriceBand, Record<PlanKey, number>>> = {
  EUR: {
    // pro_{monthly,yearly}_eur_eu — VAT-inclusive.
    eu: { monthly: 5900, yearly: 47900 },
    // pro_{monthly,yearly}_eur_row — its own stored price, no arithmetic.
    row: { monthly: 5900, yearly: 41900 },
  },
  USD: {
    // pro_{monthly,yearly}_usd_eu — the €479 membership shown in dollars (VAT-inclusive).
    eu: { monthly: 6900, yearly: 57000 },
    // pro_{monthly,yearly}_usd_row
    row: { monthly: 6900, yearly: 49900 },
  },
};

/** How many months one billing period covers — the savings maths depends on it. */
export const PLAN_MONTHS: Record<PlanKey, number> = { monthly: 1, yearly: 12 };

/** "then €479.00 <cycle>." */
export const PLAN_CYCLE_LABEL: Record<PlanKey, string> = {
  monthly: 'every month',
  yearly: 'every year',
};

/** The line under the big number on each plan card (legacy modal / /join register). */
export const PLAN_PER_LABEL: Record<PlanKey, string> = {
  monthly: 'per month',
  yearly: 'per year',
};

/** Labels are `Annual` / `Monthly` only (spec §2, brief 2026-09-14). */
export const PLAN_NAME: Record<PlanKey, string> = {
  monthly: 'Monthly',
  yearly: 'Annual',
};

/**
 * The product name on every surface that names what was bought (r15, Lars
 * 2026-09-15): the order summary, the success card, the legacy modal summary.
 * The pricing-card and toggle labels keep PLAN_NAME.
 */
export const PRODUCT_NAME: Record<PlanKey, string> = {
  monthly: 'Monthly Pro Membership',
  yearly: 'Annual Pro Membership',
};

/** Annual first — every surface. */
export const PLAN_ORDER: PlanKey[] = ['yearly', 'monthly'];

/** Yearly carries the "Best value" flag — Lars's call, 2026-08-27. */
export const FLAGGED_PLAN: PlanKey = 'yearly';

/** URL param (`annual` | `monthly`) ⇄ internal key. Anything else → null. */
export type CheckoutPlanParam = 'annual' | 'monthly';
export function planFromParam(value: string | null | undefined): PlanKey | null {
  if (value === 'annual') return 'yearly';
  if (value === 'monthly') return 'monthly';
  return null;
}
export function planToParam(plan: PlanKey): CheckoutPlanParam {
  return plan === 'yearly' ? 'annual' : 'monthly';
}
